import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type EntityInput, type RelationKind, type SubjectStory, type SubjectSummary } from '../api';
import { SourceLine, TitleButton } from '../components/cards';
import { Chip, ErrorState, Loading, Notice, useAsync } from '../components/ui';
import { formatDateTime, formatLocalDayShort, localToday } from '../format';
import { EntityEditor, KIND_LABELS } from './Subjects';

const RELATION_LABELS: Record<RelationKind, string> = {
  brand_of: 'ist Marke von',
  subsidiary_of: 'ist Tochter von',
  formerly: 'hiess früher',
  part_of: 'gehört zu',
};

const INVERSE_LABELS: Record<RelationKind, string> = {
  brand_of: 'hat die Marke',
  subsidiary_of: 'hat die Tochter',
  formerly: 'heisst heute',
  part_of: 'umfasst',
};

/** The matcher's stored reasons (internal/subjects/match.go), in the reader's language. */
const REASON_TEXT: Record<string, string> = {
  'named in the headline': 'im Titel genannt',
  'named in the lead and again in the text': 'im Lead und erneut im Text genannt',
  'mentioned; not in the headline or the lead': 'nur erwähnt, nicht im Titel oder Lead',
  'only an ambiguous name occurs; not counted until confirmed': 'nur ein mehrdeutiger Name; zählt erst nach deiner Bestätigung',
};

/**
 * One subject: the stories in which it is the main actor, the passing
 * mentions, and the unclear candidates (ambiguous names) for the reader to
 * decide. Stories come from different story clusters and days.
 */
export function SubjectPage() {
  const { id = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.subject(id), [id]);
  const [editing, setEditing] = useState(false);
  if (loading && !data) return <Loading label="Subjekt wird geladen…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return null;
  const s = data.subject;
  const e = s.entity;
  return (
    <>
      <header className="page-intro">
        <p className="eyebrow">
          <Link to="/subjekte">Deine Subjekte</Link> · {KIND_LABELS[e.kind]}
          {e.public_role ? ` · ${e.public_role}` : ''}
        </p>
        <h1>{e.canonical_name}</h1>
        <p className="page-lead">
          {s.counts.main ?? 0} Meldungen mit {e.canonical_name} als Hauptakteur, {s.counts.mention ?? 0} Erwähnungen
          {(s.counts.unresolved ?? 0) > 0 ? `, ${s.counts.unresolved} unklar` : ''}.
        </p>
        {e.fixture && (
          <Notice kind="warn" title="Testdaten">
            Dieses Subjekt ist als Test- oder Beispieldatensatz markiert.
          </Notice>
        )}
      </header>

      <div className="subject-grid">
        <div>
          <StoryGroup
            title="Hauptakteur"
            empty="Noch keine Meldung, in der das Subjekt im Titel oder im Lead steht und wiederholt genannt wird."
            items={data.main}
            entityId={e.id}
            onDecided={reload}
          />
          <StoryGroup title="Erwähnt" empty="Keine Erwähnungen." items={data.mentions} entityId={e.id} onDecided={reload} />
          {data.unresolved.length > 0 && (
            <StoryGroup
              title="Unklar: nur ein mehrdeutiger Name"
              note="Diese Meldungen enthalten nur einen mehrdeutigen Namen. Sie zählen nicht, bis du entscheidest. Eine geprüfte Modellbestätigung ist noch nicht in Betrieb."
              empty=""
              items={data.unresolved}
              entityId={e.id}
              onDecided={reload}
              decide
            />
          )}
        </div>
        <aside>
          <FollowControls summary={s} onChanged={reload} />
          <section className="panel">
            <h2>Namen und Kennungen</h2>
            {!editing && (
              <>
                <ul className="plain small">
                  {e.aliases.map((a) => (
                    <li key={a.id ?? a.alias}>
                      {a.alias} {a.ambiguous && <Chip kind="warn">mehrdeutig</Chip>}
                    </li>
                  ))}
                </ul>
                {e.identifiers.length > 0 && (
                  <ul className="plain small">
                    {e.identifiers.map((x) => (
                      <li key={x.scheme + x.value}>
                        {x.scheme.toUpperCase()}: {x.value} <span className="muted">({x.origin})</span>
                      </li>
                    ))}
                  </ul>
                )}
                {e.instruments.length > 0 && (
                  <>
                    <h3 className="mini-h">Wertpapiere</h3>
                    <ul className="plain small">
                      {e.instruments.map((x) => (
                        <li key={x.id}>
                          {x.isin ?? ''} {x.ticker ? `${x.ticker}${x.exchange ? ` (${x.exchange})` : ''}` : ''}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <button type="button" className="small-button" onClick={() => setEditing(true)}>
                  Bearbeiten
                </button>
              </>
            )}
            {editing && (
              <EntityEditor
                initial={toInput(s)}
                submitLabel="Speichern"
                onCancel={() => setEditing(false)}
                onSubmit={async (input) => {
                  try {
                    await api.updateSubject(e.id, input);
                    setEditing(false);
                    reload();
                    return null;
                  } catch (err) {
                    return err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
                  }
                }}
              />
            )}
          </section>
          <Relations summary={s} onChanged={reload} />
          {s.matching && (
            <p className="muted small">
              Abgleich der gespeicherten Meldungen ab {formatLocalDayShort(localToday(new Date(s.matching.backfill_from)))}:{' '}
              {s.matching.caught_up_at ? `aktuell (${formatDateTime(s.matching.caught_up_at)})` : `läuft, ${s.matching.scanned} Revisionen geprüft`}. Regel{' '}
              {s.matching.matcher_version}.
            </p>
          )}
        </aside>
      </div>
    </>
  );
}

function toInput(s: SubjectSummary): EntityInput {
  const e = s.entity;
  return {
    kind: e.kind,
    canonical_name: e.canonical_name,
    description: e.description ?? null,
    country: e.country ?? null,
    public_role: e.public_role ?? null,
    aliases: e.aliases.map((a) => ({ alias: a.alias, ambiguous: a.ambiguous, kind: a.kind })),
    identifiers: e.identifiers.map((x) => ({ scheme: x.scheme, value: x.value, origin: x.origin })),
    instruments: e.instruments.map((x) => ({ isin: x.isin ?? null, ticker: x.ticker ?? null, exchange: x.exchange ?? null, origin: x.origin })),
  };
}

function StoryGroup({
  title,
  note,
  empty,
  items,
  entityId,
  onDecided,
  decide = false,
}: {
  title: string;
  note?: string;
  empty: string;
  items: SubjectStory[];
  entityId: string;
  onDecided: () => void;
  decide?: boolean;
}) {
  return (
    <section aria-label={title} className="subject-stories">
      <h2>
        {title} <span className="muted small">({items.length})</span>
      </h2>
      {note && <p className="muted small">{note}</p>}
      {items.length === 0 && empty && <p className="muted">{empty}</p>}
      <ul className="story-rows">
        {items.map((st) => (
          <StoryItem key={st.card_id} st={st} entityId={entityId} onDecided={onDecided} decide={decide} />
        ))}
      </ul>
    </section>
  );
}

function StoryItem({ st, entityId, onDecided, decide }: { st: SubjectStory; entityId: string; onDecided: () => void; decide: boolean }) {
  const [busy, setBusy] = useState(false);
  const act = async (role: 'main' | 'mention' | 'rejected') => {
    setBusy(true);
    try {
      await api.decideMatch(entityId, st.match_id, role);
      onDecided();
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="story-row">
      <SourceLine view={st.card} showTopic />
      <TitleButton view={st.card} as="h3" />
      {st.evidence?.quote && (
        <p className="evidence-quote">
          «{st.evidence.quote}»{' '}
          <span className="muted small">
            ({REASON_TEXT[st.evidence.reason ?? ''] ?? st.evidence.reason ?? ''}
            {st.via ? `, über ${st.via}` : ''}
            {st.decided_by === 'reader' ? ', von dir entschieden' : ''})
          </span>
        </p>
      )}
      {decide && (
        <div className="actions" role="group" aria-label="Gehört diese Meldung zum Subjekt?">
          <button type="button" className="small-button" disabled={busy} onClick={() => act('main')}>
            Ja, Hauptakteur
          </button>
          <button type="button" className="small-button" disabled={busy} onClick={() => act('mention')}>
            Ja, erwähnt
          </button>
          <button type="button" className="small-button" disabled={busy} onClick={() => act('rejected')}>
            Nein, anderes
          </button>
        </div>
      )}
    </li>
  );
}

function FollowControls({ summary, onChanged }: { summary: SubjectSummary; onChanged: () => void }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const f = summary.follow;
  const id = summary.entity.id;
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  if (!f) {
    return (
      <section className="panel">
        <h2>Nicht verfolgt</h2>
        <button type="button" className="button primary" disabled={busy} onClick={() => run(() => api.createSubject({ entity_id: id }))}>
          Subjekt verfolgen
        </button>
      </section>
    );
  }
  return (
    <section className="panel">
      <h2>Verfolgung</h2>
      <p className="small">
        {f.status === 'paused'
          ? 'Pausiert: keine Hervorhebung in Ausgaben, kein Platz unter „Deine Subjekte“. Die Seite hier bleibt aktuell.'
          : 'Aktiv: Meldungen mit diesem Subjekt als Hauptakteur werden in Ausgaben höher eingestuft und unter „Deine Subjekte“ genannt. Erwähnungen lösen nichts aus.'}
      </p>
      <div className="button-row">
        <button
          type="button"
          className="small-button"
          disabled={busy}
          onClick={() => run(() => api.setSubjectFollow(id, { status: f.status === 'paused' ? 'active' : 'paused' }))}
        >
          {f.status === 'paused' ? 'Fortsetzen' : 'Pausieren'}
        </button>
        <button
          type="button"
          className="text-button danger"
          disabled={busy}
          onClick={() =>
            run(async () => {
              await api.stopSubject(id);
              navigate('/subjekte');
            })
          }
        >
          Nicht mehr verfolgen
        </button>
      </div>
      <label className="small">
        <input
          type="checkbox"
          checked={Boolean(f.materiality?.telegram)}
          disabled={busy}
          onChange={(e) => run(() => api.setSubjectFollow(id, { telegram: e.target.checked }))}
        />{' '}
        Telegram-Hinweis gewünscht
      </label>
      <p className="muted small">Nur als Wunsch gespeichert: Hinweise zu Subjekten werden in dieser Version nicht versendet.</p>
      {error && <Notice kind="danger">{error}</Notice>}
    </section>
  );
}

function Relations({ summary, onChanged }: { summary: SubjectSummary; onChanged: () => void }) {
  const e = summary.entity;
  const included = new Set(summary.follow?.materiality?.include_related ?? []);
  const [relation, setRelation] = useState<RelationKind>('brand_of');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const known = useAsync(() => api.entities(), []);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };
  const add = () => {
    const match = (known.data?.entities ?? []).find((x) => x.canonical_name.toLowerCase() === name.trim().toLowerCase() && x.id !== e.id);
    return run(async () => {
      await api.addRelation(
        e.id,
        match
          ? { relation, related_entity_id: match.id }
          : { relation, new_entity: { kind: 'company', canonical_name: name.trim(), aliases: [], identifiers: [], instruments: [] } },
      );
      setName('');
    });
  };
  return (
    <section className="panel">
      <h2>Beziehungen</h2>
      <p className="muted small">Verwandte zählen nur, wenn du sie für dieses Subjekt einschliesst.</p>
      {e.relations.length === 0 && <p className="muted small">Noch keine Beziehungen.</p>}
      <ul className="plain small">
        {e.relations.map((r) => (
          <li key={r.id}>
            {(r.inverse ? INVERSE_LABELS : RELATION_LABELS)[r.relation]} <Link to={`/subjekte/${r.related_entity_id}`}>{r.related_name}</Link>{' '}
            {summary.follow && (
              <label>
                <input
                  type="checkbox"
                  checked={included.has(r.related_entity_id)}
                  disabled={busy}
                  onChange={(ev) => {
                    const next = new Set(included);
                    if (ev.target.checked) next.add(r.related_entity_id);
                    else next.delete(r.related_entity_id);
                    run(() => api.setSubjectFollow(e.id, { include_related: [...next] }));
                  }}
                />{' '}
                einschliessen
              </label>
            )}{' '}
            <button type="button" className="text-button danger" disabled={busy} onClick={() => run(() => api.removeRelation(e.id, r.id))}>
              Entfernen
            </button>
          </li>
        ))}
      </ul>
      <div className="id-row">
        <select aria-label="Art der Beziehung" value={relation} onChange={(ev) => setRelation(ev.target.value as RelationKind)}>
          {(Object.keys(RELATION_LABELS) as RelationKind[]).map((k) => (
            <option key={k} value={k}>
              {RELATION_LABELS[k]}
            </option>
          ))}
        </select>
        <input type="text" aria-label="Verwandtes Subjekt" list="known-entities" value={name} onChange={(ev) => setName(ev.target.value)} placeholder="Name" />
        <datalist id="known-entities">
          {(known.data?.entities ?? []).filter((x) => x.id !== e.id).map((x) => (
            <option key={x.id} value={x.canonical_name} />
          ))}
        </datalist>
        <button type="button" className="small-button" disabled={busy || name.trim().length < 2} onClick={add}>
          Hinzufügen
        </button>
      </div>
      {error && <Notice kind="danger">{error}</Notice>}
    </section>
  );
}
